import { calculateComplianceScore } from '../../utils/Nutrition';

const memberId = user => user?._id || user?.user_id || user?.id;
const nonnegative = value => Number.isFinite(value) && value >= 0;
const positive = value => Number.isFinite(value) && value > 0;

// Consume the unfiltered, viewer-authorized dojo feed. Never substitute a viewer
// target, profile formula or shared meal template for a member/day target.
export function buildDojoNutritionMembers({ users = [], activities = [], dateKey, todayKey, currentUserId, personal }) {
  const roster = new Map();
  users.forEach(user => { if (memberId(user)) roster.set(String(memberId(user)), user); });
  const uniqueMeals = new Map();
  activities.forEach(a => {
    if (a?._id && a.type === 'meal' && !a.is_template && ['complete', 'completed'].includes(a.status) && a.dojo_nutrition?.day_key === dateKey) uniqueMeals.set(String(a._id), a);
  });
  return [...roster].map(([id, user]) => {
    if (id === String(currentUserId) && personal) {
      const { totals, targets, score } = personal;
      const calMin = targets?.calories_min ?? targets?.calories;
      const calMax = targets?.calories_max ?? targets?.calories;
      const usable = totals && targets && nonnegative(totals.calories) && nonnegative(totals.protein) && positive(calMin) && positive(calMax) && positive(targets.protein);
      const status = !usable ? 'unavailable' : totals.calories > calMax ? 'above'
        : totals.calories < calMin || totals.protein < targets.protein || (targets.water_ml > 0 && (totals.water_ml || 0) < targets.water_ml)
          ? (dateKey === todayKey ? 'building' : 'below') : 'on-target';
      return { id, user, isSelf: true, totals: usable ? totals : null, targets, score: usable ? score : null, calMin, calMax, status, coverage: 'personal' };
    }
    const meals = [...uniqueMeals.values()].filter(a => String(a.user_id) === id);
    const targets = meals[0]?.dojo_nutrition?.targets;
    // Same fallback range as the canonical score, only with a real member target.
    const calMin = targets?.calories_min ?? Math.round(targets?.calories * 0.9);
    const calMax = targets?.calories_max ?? Math.round(targets?.calories * 1.1);
    const targetValid = positive(calMin) && positive(calMax) && calMin <= calMax && positive(targets?.protein) && (targets.water_ml == null || nonnegative(targets.water_ml));
    const nutrients = ['calories', 'protein', ...(targets?.water_ml > 0 ? ['water_ml'] : [])];
    const usable = targetValid && meals.length > 0 && meals.every(m => !m.nutrition_summary_unavailable && nutrients.every(k => nonnegative(m.nutrition_summary?.[k])));
    const optionalNutrients = ['carbs', 'fat'].filter(k => meals.length && meals.every(m => nonnegative(m.nutrition_summary?.[k])));
    const totals = usable ? meals.reduce((sum, m) => {
      [...nutrients, ...optionalNutrients].forEach(k => { sum[k] = (sum[k] || 0) + m.nutrition_summary[k]; });
      return sum;
    }, { calories: 0, protein: 0, water_ml: 0 }) : null;
    let status = 'unavailable';
    if (usable) {
      if (totals.calories > calMax) status = 'above';
      else if (totals.calories < calMin || totals.protein < targets.protein || totals.water_ml < (targets.water_ml || 0)) status = (dateKey === todayKey || meals.some(m => m.dojo_nutrition.is_current_day)) ? 'building' : 'below';
      else status = 'on-target';
    }
    return { id, user, isSelf: id === String(currentUserId), targets: targetValid ? targets : null, totals, calMin, calMax, status, coverage: 'partial', score: usable ? calculateComplianceScore(totals, targets) : null, mealCount: meals.length };
  }).sort((a, b) => Number(b.isSelf) - Number(a.isSelf));
}
